import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms';
import { mediaApi, type MediaItem } from '@/services/media';

import styles from './MediaPicker.module.css';

export interface MediaPickerProps {
  open: boolean;
  onClose: () => void;
  onPick: (media: MediaItem) => void;
}

type Mode = 'upload' | 'url';

export function MediaPicker({ open, onClose, onPick }: MediaPickerProps) {
  const { t } = useTranslation(['common', 'nav', 'board', 'articles', 'analytics', 'feeddrive', 'config', 'strategist']);
  void t;
  const [mode, setMode] = useState<Mode>('upload');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  if (!open) return null;

  async function handleUpload(file: File) {
    setBusy(true);
    setError(null);
    try {
      const media = await mediaApi.uploadFile(file);
      onPick(media);
      onClose();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleUrl() {
    if (!url.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const media = await mediaApi.uploadFromUrl(url.trim());
      onPick(media);
      onClose();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Trocar imagem destacada</h2>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Fechar"
          >
            ×
          </button>
        </header>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${mode === 'upload' ? styles.tabActive : ''}`}
            onClick={() => setMode('upload')}
          >
            Upload
          </button>
          <button
            className={`${styles.tab} ${mode === 'url' ? styles.tabActive : ''}`}
            onClick={() => setMode('url')}
          >
            Por URL
          </button>
        </div>

        <div className={styles.body}>
          {mode === 'upload' && (
            <div className={styles.uploadArea}>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUpload(file);
                }}
              />
              <Button onClick={() => fileRef.current?.click()} disabled={busy} loading={busy}>
                Escolher arquivo
              </Button>
              <p className={styles.hint}>JPG, PNG ou WebP.</p>
            </div>
          )}

          {mode === 'url' && (
            <div className={styles.urlArea}>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://…"
                className={styles.urlInput}
              />
              <Button onClick={handleUrl} disabled={busy || !url.trim()} loading={busy}>
                Importar
              </Button>
            </div>
          )}

          {error && <p className={styles.error}>{error}</p>}
        </div>
      </div>
    </div>
  );
}
